import { defineStore } from "pinia";
import {
  createGenerationJob,
  getLatestGenerationJobForPet,
  getPetPackageStatus,
  getGenerationJob,
  regenerateGenerationAction,
  syncGenerationJob,
  uploadPetPhoto,
  createPaymentOrder,
  createDesktopBinding,
  getPaymentOrder,
  getPaymentProducts,
  listGenerationActions,
  ApiRequestError,
  isUnauthorizedApiError,
  type GenerationJob,
  type GenerationVideoClip,
  type DesktopBindingCode,
  type PaymentOrder,
  type PaymentProduct,
  type PetPhotoAsset,
  type AccountState,
  type SkinPackageStatus,
  type PetActionKey
} from "../generation-api";
import { runtimeConfig } from "../runtime-config";
import { useAuthStore, type AuthQuota } from "./auth";
import { preparePetPhotoForUpload } from "../image-compression";
import { t } from "../i18n";

export type ActionStatus = "idle" | "queued" | "generating" | "complete" | "error";

export interface ActionDefinition {
  key: PetActionKey;
  label: string;
  defaultSelected: boolean;
  locked: boolean;
  paid: boolean;
  creditCost?: number;
}

export interface ActionResult {
  key: PetActionKey;
  label: string;
  status: ActionStatus;
  video?: GenerationVideoClip;
  error?: string;
  regenerateUsed: boolean;
}

const actionCatalog: ActionDefinition[] = [
  {
    key: "idle",
    label: t("generate.builtInActions.idle"),
    defaultSelected: true,
    locked: true,
    paid: false
  },
  {
    key: "walk",
    label: t("generate.builtInActions.walk"),
    defaultSelected: true,
    locked: true,
    paid: false
  },
  {
    key: "happy",
    label: t("generate.builtInActions.happy"),
    defaultSelected: false,
    locked: false,
    paid: true
  },
  {
    key: "eat",
    label: t("generate.builtInActions.eat"),
    defaultSelected: false,
    locked: false,
    paid: true
  },
  {
    key: "sleep",
    label: t("generate.builtInActions.sleep"),
    defaultSelected: false,
    locked: false,
    paid: true
  }
];
const allowedPhotoTypes = ["image/png", "image/jpeg", "image/webp"];
const maxPhotoFileSizeBytes = 15 * 1024 * 1024;
const maxPhotoWidth = 4096;
const maxPhotoHeight = 4096;
const rejectedCustomDescriptionTerms = [
  "血",
  "流血",
  "受伤",
  "伤害",
  "残忍",
  "死亡",
  "断肢",
  "断腿",
  "骨折",
  "肢解",
  "多腿",
  "额外腿",
  "第五条腿",
  "五条腿",
  "畸形",
  "恐怖",
  "惊悚",
  "后空翻",
  "翻滚",
  "跑酷",
  "杂技",
  "变身",
  "飞行",
  "blood",
  "gore",
  "injury",
  "extra leg",
  "multiple legs",
  "deformed",
  "horror",
  "backflip",
  "parkour",
  "flying"
];

function createInitialResults(): Record<PetActionKey, ActionResult> {
  return actionCatalog.reduce(
    (result, action) => {
      result[action.key] = {
        key: action.key,
        label: action.label,
        status: "idle",
        regenerateUsed: false
      };
      return result;
    },
    {} as Record<PetActionKey, ActionResult>
  );
}

export const usePetGenerationStore = defineStore("petGeneration", {
  state: () => ({
    actions: actionCatalog,
    selectedActionKeys: actionCatalog.filter((action) => action.defaultSelected).map((action) => action.key),
    petName: "",
    userAlias: "",
    customDescription: "",
    photoFile: null as File | null,
    photoAsset: null as PetPhotoAsset | null,
    photoUrl: "",
    cutoutPhotoUrl: "",
    isUploadingPhoto: false,
    progress: 0,
    progressMessage: t("store.initialProgress"),
    isGenerating: false,
    isSyncing: false,
    syncStatus: "waiting" as "waiting" | "ready" | "syncing" | "synced" | "error",
    errorMessage: "",
    currentJobId: "",
    currentPetId: "",
    selectedExistingPetId: "",
    creationMode: "new" as "new" | "existing",
    packageStatus: null as SkinPackageStatus | null,
    desktopSyncCopied: false,
    results: createInitialResults(),
    previewVideo: null as GenerationVideoClip | null,
    paymentProducts: [] as PaymentProduct[],
    pendingPaymentOrder: null as PaymentOrder | null,
    pendingPaymentIntent: null as "credits" | "pet_slot" | "regenerate" | null,
    pendingRegenerateActionKey: null as PetActionKey | null,
    desktopBinding: null as DesktopBindingCode | null,
    isPaymentLoading: false,
    paymentDialog: null as { title: string; message: string; intent: "credits" | "pet_slot" | "regenerate" } | null,
    quota: {
      generationCredits: 2,
      petLimit: 1,
      petCount: 0
    }
  }),
  getters: {
    selectedActions: (state) => state.actions.filter((action) => state.selectedActionKeys.includes(action.key)),
    paidExtraActionCount: (state) =>
      state.actions.filter((action) => state.selectedActionKeys.includes(action.key) && action.paid).length,
    estimatedCreditCost(): number {
      const actionCredits = this.selectedActions.reduce((sum, action) => sum + (action.creditCost ?? 1), 0);
      return actionCredits + (this.customDescription.trim() ? 1 : 0);
    },
    canCreatePet: (state) => state.quota.petCount < state.quota.petLimit,
    isUsingExistingPet: (state) => state.creationMode === "existing" && Boolean(state.selectedExistingPetId),
    canSync: (state) => state.selectedActionKeys.every((key) => state.results[key]?.status === "complete"),
    dialogProducts: (state) => {
      if (!state.paymentDialog) {
        return [];
      }
      if (state.paymentDialog.intent === "pet_slot") {
        return state.paymentProducts.filter((product) => product.productType === "pet_slot");
      }
      if (state.paymentDialog.intent === "regenerate" && state.quota.generationCredits > 0) {
        return [];
      }
      return state.paymentProducts.filter((product) => product.productType === "credits");
    },
    canUseCreditForRegenerate: (state) =>
      state.paymentDialog?.intent === "regenerate"
      && Boolean(state.pendingRegenerateActionKey)
      && state.quota.generationCredits > 0
  },
  actions: {
    async loadActions() {
      try {
        const list = await listGenerationActions();
        if (list.length === 0) {
          return;
        }
        this.actions = list.map((item) => ({
          key: item.key,
          label: item.name,
          defaultSelected: item.isDefault,
          locked: item.isDefault,
          paid: item.creditCost > 0,
          creditCost: item.creditCost
        }));
        this.selectedActionKeys = this.actions.filter((action) => action.defaultSelected).map((action) => action.key);
        this.results = this.actions.reduce(
          (acc, action) => {
            acc[action.key] = { key: action.key, label: action.label, status: "idle", regenerateUsed: false };
            return acc;
          },
          {} as Record<PetActionKey, ActionResult>
        );
      } catch {
        // 加载失败时保留内置默认动作目录。
      }
    },
    async openPaymentDialog(intent: "credits" | "pet_slot" | "regenerate", actionKey?: PetActionKey) {
      const copy = {
        credits: {
          title: t("store.buyCreditsTitle"),
          message: t("store.buyCreditsMessage", { provider: runtimeConfig.market.paymentProviderLabel })
        },
        pet_slot: {
          title: t("store.buyPetSlotTitle"),
          message: t("store.buyPetSlotMessage", { provider: runtimeConfig.market.paymentProviderLabel })
        },
        regenerate: {
          title: t("store.regenerateTitle"),
          message: t("store.regenerateMessage")
        }
      }[intent];
      this.errorMessage = "";
      this.paymentDialog = { ...copy, intent };
      this.pendingRegenerateActionKey = intent === "regenerate" ? actionKey ?? this.pendingRegenerateActionKey : null;
      await this.loadPaymentProducts();
    },
    async loadPaymentProducts() {
      if (this.isPaymentLoading) {
        return;
      }

      this.isPaymentLoading = true;
      try {
        const productType = this.paymentDialog?.intent === "pet_slot" ? "pet_slot" : "credits";
        this.paymentProducts = await getPaymentProducts(productType);
      } catch (error) {
        this.errorMessage = messageFromRequestError(error, t("store.productsLoadFailed"));
      } finally {
        this.isPaymentLoading = false;
      }
    },
    async purchaseProduct(product: PaymentProduct) {
      this.isPaymentLoading = true;
      this.errorMessage = "";
      const paymentIntent = this.paymentDialog?.intent;
      const regenerateActionKey = this.pendingRegenerateActionKey;
      try {
        const order = await createPaymentOrder(product.productId, runtimeConfig.market.paymentChannel);
        this.pendingPaymentOrder = order;
        this.pendingPaymentIntent = paymentIntent ?? null;
        if (runtimeConfig.market.paymentChannel === "stripe") {
          const paymentUrl = order.paymentUrl?.trim();
          if (!paymentUrl) {
            throw new Error(t("store.openPaymentFailed"));
          }

          this.progressMessage = t("store.openingStripe");
          redirectToPaymentPage(paymentUrl);
          return;
        }
        if (runtimeConfig.market.paymentChannel === "wechat_pay" && order.status !== "paid") {
          this.progressMessage = order.message || t("store.orderWaiting", { provider: runtimeConfig.market.paymentProviderLabel });
          return;
        }
        const paidOrder = await this.pollPaymentOrder(order.orderId);
        this.pendingPaymentOrder = paidOrder;
        if (paidOrder.status !== "paid") {
          this.progressMessage = paidOrder.message || t("store.orderWaiting", { provider: runtimeConfig.market.paymentProviderLabel });
          return;
        }
        await this.applyPaidOrderState(paidOrder);
        this.progressMessage = paidOrder.message || t("store.rightsArrived");
        this.paymentDialog = null;
        this.pendingPaymentOrder = null;
        this.pendingPaymentIntent = null;
        if (paymentIntent === "regenerate" && regenerateActionKey) {
          await this.regenerateAction(regenerateActionKey, true);
        }
      } catch (error) {
        this.errorMessage = paymentErrorMessage(error, t("store.paymentFailed"));
      } finally {
        this.isPaymentLoading = false;
      }
    },
    async refreshPendingPaymentOrder(silent = false) {
      if (!this.pendingPaymentOrder) {
        if (!silent) {
          this.errorMessage = t("store.noPendingOrder");
        }
        return;
      }

      if (!silent) {
        this.isPaymentLoading = true;
        this.errorMessage = "";
      }
      try {
        const codeUrl = this.pendingPaymentOrder.paymentUrl ?? null;
        const latest = await getPaymentOrder(this.pendingPaymentOrder.orderId);
        // GetOrder 不会重新返回支付链接，待支付时保留下单时拿到的链接。
        this.pendingPaymentOrder =
          latest.status === "pending" ? { ...latest, paymentUrl: latest.paymentUrl ?? codeUrl } : latest;
        if (latest.status !== "paid") {
          this.progressMessage = latest.message || t("store.orderStillWaiting", { provider: runtimeConfig.market.paymentProviderLabel });
          return;
        }

        await this.applyPaidOrderState(latest);
        const pendingIntent = this.pendingPaymentIntent ?? this.paymentDialog?.intent ?? null;
        const regenerateActionKey = this.pendingRegenerateActionKey;
        this.progressMessage = latest.message || t("store.rightsArrived");
        this.paymentDialog = null;
        this.pendingPaymentOrder = null;
        this.pendingPaymentIntent = null;
        if (pendingIntent === "regenerate" && regenerateActionKey) {
          await this.regenerateAction(regenerateActionKey, true);
        }
      } catch (error) {
        if (!silent) {
          this.errorMessage = messageFromRequestError(error, t("store.refreshPaymentFailed"));
        }
      } finally {
        if (!silent) {
          this.isPaymentLoading = false;
        }
      }
    },
    async pollPaymentOrder(orderId: string, attempts = 8) {
      // 下单时拿到的支付链接，轮询 GetOrder 不会再返回，需在待支付期间保留。
      const codeUrl = this.pendingPaymentOrder?.paymentUrl ?? null;
      let latest = await getPaymentOrder(orderId);
      for (let index = 0; index < attempts && latest.status === "pending"; index += 1) {
        this.pendingPaymentOrder = { ...latest, paymentUrl: latest.paymentUrl ?? codeUrl };
        await waitForPaymentStep();
        latest = await getPaymentOrder(orderId);
      }
      return latest.status === "pending" ? { ...latest, paymentUrl: latest.paymentUrl ?? codeUrl } : latest;
    },
    async setPhoto(file: File) {
      if (!allowedPhotoTypes.includes(file.type)) {
        this.errorMessage = t("store.unsupportedPhoto");
        this.progressMessage = this.errorMessage;
        return;
      }

      if (this.photoUrl) {
        URL.revokeObjectURL(this.photoUrl);
      }
      this.photoAsset = null;
      this.cutoutPhotoUrl = "";
      this.photoFile = file;
      this.photoUrl = URL.createObjectURL(file);
      this.progress = 0;
      this.progressMessage = t("store.photoReadyToCheck");
      this.errorMessage = "";
    },
    toggleAction(key: PetActionKey) {
      const action = this.actions.find((item) => item.key === key);
      if (!action || action.defaultSelected || action.locked || this.isGenerating) {
        return;
      }
      if (this.selectedActionKeys.includes(key)) {
        this.selectedActionKeys = this.selectedActionKeys.filter((item) => item !== key);
        return;
      }
      this.selectedActionKeys = [...this.selectedActionKeys, key];
    },
    async selectExistingPet(petId: string) {
      const auth = useAuthStore();
      const pet = auth.pets.find((item) => item.petId === petId);
      if (!pet) {
        this.errorMessage = t("store.petNotFound");
        return;
      }

      this.creationMode = "existing";
      this.selectedExistingPetId = pet.petId;
      this.currentPetId = pet.petId;
      this.petName = pet.petName;
      this.userAlias = pet.userAlias;
      this.packageStatus = null;
      this.desktopBinding = null;
      this.progressMessage = t("store.continuePet", { petName: pet.petName, userAlias: pet.userAlias });
      await this.restoreLatestJobForCurrentPet();
    },
    startNewPet() {
      this.creationMode = "new";
      this.selectedExistingPetId = "";
      this.currentPetId = "";
      this.packageStatus = null;
      this.desktopBinding = null;
      if (!this.canCreatePet) {
        void this.openPaymentDialog("pet_slot");
        return;
      }

      this.petName = "";
      this.userAlias = "";
      this.progressMessage = t("store.newPetReady");
    },
    validateForm() {
      if (this.creationMode === "new" && !this.canCreatePet) {
        void this.openPaymentDialog("pet_slot");
        return false;
      }
      if (this.creationMode === "existing" && !this.selectedExistingPetId) {
        this.errorMessage = t("store.chooseExistingPet");
        return false;
      }
      if (!this.photoFile) {
        this.errorMessage = t("store.uploadPhotoFirst");
        return false;
      }
      if (!this.petName.trim()) {
        this.errorMessage = t("store.petNameRequired");
        return false;
      }
      if (!this.userAlias.trim()) {
        this.errorMessage = t("store.userAliasRequired");
        return false;
      }
      const customDescription = this.customDescription.trim().toLowerCase();
      if (customDescription.length > 120) {
        this.errorMessage = t("store.customTooLong");
        return false;
      }
      if (customDescription && rejectedCustomDescriptionTerms.some((term) => customDescription.includes(term))) {
        this.errorMessage = t("store.customRejected");
        return false;
      }
      return true;
    },
    async generateSelectedActions() {
      if (!this.validateForm() || !this.photoFile) {
        return;
      }

      this.isGenerating = true;
      this.errorMessage = "";
      this.syncStatus = "waiting";
      this.desktopBinding = null;
      this.desktopSyncCopied = false;
      this.packageStatus = null;
      this.progress = 0;
      this.progressMessage = t("store.checkingPhoto", { petName: this.petName, userAlias: this.userAlias });
      for (const key of this.selectedActionKeys) {
        this.results[key] = {
          ...this.results[key],
          status: "queued",
          error: ""
        };
      }

      try {
        const photoAsset = await this.prepareAndUploadSelectedPhoto();
        const createdJob = await createGenerationJob({
          photoAssetId: photoAsset.assetId,
          petId: this.creationMode === "existing" ? this.selectedExistingPetId : undefined,
          petName: this.petName.trim(),
          userAlias: this.userAlias.trim(),
          actions: this.selectedActionKeys,
          customDescription: this.customDescription.trim()
        });
        this.currentJobId = createdJob.jobId;
        this.currentPetId = createdJob.petId;
        this.selectedExistingPetId = createdJob.petId;
        this.creationMode = "existing";
        this.applyBackendJob(createdJob);
        await this.refreshAccountState();
        let latestJob = createdJob;
        while (latestJob.status === "queued" || latestJob.status === "processing") {
          await waitForBackendStep();
          latestJob = await getGenerationJob(latestJob.jobId);
          this.applyBackendJob(latestJob);
        }
        if (latestJob.status === "error") {
          throw new Error(latestJob.message || t("store.actionFailed"));
        }
        this.progress = 100;
        this.syncStatus = "ready";
        this.progressMessage = t("store.actionsReady", { petName: this.petName, userAlias: this.userAlias });
      } catch (error) {
        const message = messageFromRequestError(error, t("store.jobFailed"));
        if (error instanceof ApiRequestError && error.code === "quota_not_enough") {
          await this.refreshAccountState().catch(() => null);
          void this.openPaymentDialog("credits");
        }
        this.errorMessage = message;
        this.progressMessage = message;
        for (const key of this.selectedActionKeys) {
          if (this.results[key].status !== "complete") {
            this.results[key] = {
              ...this.results[key],
              status: "error",
              error: message
            };
          }
        }
      } finally {
        this.isGenerating = false;
      }
    },
    async prepareAndUploadSelectedPhoto(): Promise<PetPhotoAsset> {
      if (!this.photoFile) {
        throw new Error(t("store.uploadPhotoFirst"));
      }

      this.isUploadingPhoto = true;
      this.progress = 6;
      this.progressMessage = t("store.checkingPhotoWarm");
      try {
        const preparedPhoto = await preparePetPhotoForUpload(this.photoFile, maxPhotoFileSizeBytes, maxPhotoWidth, maxPhotoHeight);
        this.photoFile = preparedPhoto.file;
        if (preparedPhoto.compressed) {
          if (this.photoUrl) {
            URL.revokeObjectURL(this.photoUrl);
          }
          this.photoUrl = URL.createObjectURL(preparedPhoto.file);
          this.progress = 16;
          this.progressMessage = t("store.photoCompressed", {
            size: formatFileSize(preparedPhoto.file.size),
            petName: this.petName || t("store.fallbackPetName")
          });
        } else {
          this.progress = 12;
          this.progressMessage = t("store.photoGood");
        }

        const asset = await uploadPetPhoto(preparedPhoto.file);
        this.photoAsset = asset;
        this.cutoutPhotoUrl = asset.cutoutImageUrl;
        this.progress = Math.max(this.progress, 24);
        this.progressMessage = asset.message || t("store.photoPrepared", { petName: this.petName || t("store.fallbackPetName") });
        return asset;
      } finally {
        this.isUploadingPhoto = false;
      }
    },
    async confirmPaidRegenerate() {
      const actionKey = this.pendingRegenerateActionKey;
      if (!actionKey) {
        this.errorMessage = t("store.chooseRegenerate");
        return;
      }
      if (this.quota.generationCredits <= 0) {
        await this.openPaymentDialog("regenerate", actionKey);
        return;
      }
      this.paymentDialog = null;
      this.pendingPaymentOrder = null;
      await this.regenerateAction(actionKey, true);
    },
    async regenerateAction(key: PetActionKey, allowPaid = false) {
      const result = this.results[key];
      if (!result) {
        return;
      }
      if (result.regenerateUsed && !allowPaid) {
        void this.openPaymentDialog("regenerate", key);
        return;
      }
      if (!this.currentJobId) {
        this.errorMessage = t("store.submitFirst");
        return;
      }

      this.isGenerating = true;
      this.progress = 0;
      this.errorMessage = "";
      this.syncStatus = "waiting";
      this.desktopBinding = null;
      this.desktopSyncCopied = false;
      this.packageStatus = null;
      this.results[key] = {
        ...result,
        status: "generating",
        error: ""
      };

      try {
        const job = await regenerateGenerationAction(this.currentJobId, key);
        let latestJob = job;
        this.applyBackendJob(latestJob);
        let latestAction = latestJob.actions.find((action) => action.key === key);
        while (
          latestJob.status !== "error" &&
          latestAction &&
          (latestAction.status === "queued" || latestAction.status === "generating")
        ) {
          this.progressMessage = t("store.rehearsingAgain", {
            action: this.actions.find((action) => action.key === key)?.label || t("store.fallbackAction")
          });
          await waitForBackendStep();
          latestJob = await getGenerationJob(latestJob.jobId);
          this.applyBackendJob(latestJob);
          latestAction = latestJob.actions.find((action) => action.key === key);
        }

        if (latestAction?.status === "error") {
          throw new Error(latestAction.error || t("store.actionFailed"));
        }

        const video = latestAction?.video;
        if (!video) {
          throw new Error(t("store.actionNoVideo"));
        }
        this.results[key] = {
          ...this.results[key],
          label: video.label,
          status: "complete",
          video,
          regenerateUsed: true,
          error: ""
        };
        await this.refreshAccountState();
        this.pendingRegenerateActionKey = null;
        this.progress = 100;
        this.progressMessage = t("store.regenerateReady", { label: video.label, userAlias: this.userAlias });
      } catch (error) {
        const message = messageFromRequestError(error, t("store.regenerateFailed"));
        if (error instanceof ApiRequestError && error.code === "quota_not_enough") {
          void this.openPaymentDialog("regenerate", key);
        }
        this.results[key] = {
          ...this.results[key],
          status: "error",
          error: message
        };
        this.errorMessage = message;
      } finally {
        this.isGenerating = false;
      }
    },
    async syncDesktopPet() {
      if (!this.canSync) {
        this.errorMessage = t("store.syncNeedVideos");
        return;
      }
      if (!this.currentJobId) {
        this.errorMessage = t("store.syncNeedJob");
        return;
      }
      this.isSyncing = true;
      this.syncStatus = "syncing";
      this.desktopBinding = null;
      this.desktopSyncCopied = false;
      this.progress = 40;
      this.progressMessage = t("store.changingSkin", { petName: this.petName || t("store.fallbackPetName") });
      try {
        const job = await syncGenerationJob(this.currentJobId);
        this.applyBackendJob(job);
        if (job.petId) {
          this.currentPetId = job.petId;
          this.packageStatus = await getPetPackageStatus(job.petId);
          while (this.packageStatus.status === "waiting" || this.packageStatus.status === "building") {
            this.progress = Math.min(95, this.progress + 8);
            this.progressMessage = warmPackageMessage(this.petName, this.userAlias, this.packageStatus.message);
            await waitForBackendStep();
            this.packageStatus = await getPetPackageStatus(job.petId);
          }
          if (this.packageStatus.status === "error") {
            throw new Error(this.packageStatus.message || t("store.packageFailed"));
          }
        }
        this.progress = 100;
        this.syncStatus = this.packageStatus?.status === "ready" ? "synced" : "syncing";
        this.progressMessage = desktopPairingReadyMessage(this.petName, this.userAlias);
        if (this.syncStatus === "synced") {
          await this.createDesktopPairingCode(false);
        }
      } catch (error) {
        this.syncStatus = "error";
        this.errorMessage = messageFromRequestError(error, t("store.syncFailed"));
      } finally {
        this.isSyncing = false;
      }
    },
    async createDesktopPairingCode(copyToClipboard = true) {
      if (!this.currentPetId) {
        this.errorMessage = t("store.noPetToSync");
        return;
      }

      this.errorMessage = "";
      try {
        const binding = await createDesktopBinding(this.currentPetId);
        this.desktopBinding = binding;
        if (copyToClipboard && navigator.clipboard) {
          const params = new URLSearchParams({
            apiBaseUrl: runtimeConfig.publicApiBaseUrl,
            pairCode: binding.pairingCode
          });
          await navigator.clipboard.writeText(params.toString());
          this.desktopSyncCopied = true;
          this.progressMessage = desktopPairingReadyMessage(this.petName, this.userAlias, true);
          return;
        }
        this.desktopSyncCopied = false;
        this.progressMessage = desktopPairingReadyMessage(this.petName, this.userAlias);
      } catch (error) {
        this.errorMessage = messageFromRequestError(error, t("store.pairingFailed"));
      }
    },
    applyBackendJob(job: GenerationJob) {
      this.progress = Math.max(this.progress, job.progress);
      this.progressMessage = warmJobMessage(this.petName, this.userAlias, job.message);
      this.currentPetId = job.petId || this.currentPetId;
      for (const action of job.actions) {
        this.results[action.key] = {
          ...this.results[action.key],
          label: action.label,
          status: action.status,
          video: action.video,
          regenerateUsed: Boolean(action.regenerateUsed || this.results[action.key]?.regenerateUsed),
          error: action.error || ""
        };
      }
    },
    applyQuota(quota: AuthQuota) {
      this.quota = {
        generationCredits: quota.generationCredits,
        petLimit: quota.petLimit,
        petCount: quota.petCount
      };
      const auth = useAuthStore();
      auth.quota = {
        generationCredits: quota.generationCredits,
        petLimit: quota.petLimit,
        petCount: quota.petCount,
        canCreatePet: quota.canCreatePet
      };
    },
    async applyPaidOrderState(order: PaymentOrder) {
      if (order.quota) {
        this.applyQuota(order.quota);
        await this.refreshAccountState();
        return;
      }

      const account = await this.refreshAccountState();
      if (!account?.quota) {
        throw new Error(t("store.paidButQuotaMissing"));
      }
    },
    async refreshAccountState(): Promise<AccountState | null> {
      const auth = useAuthStore();
      try {
        const account = await auth.refreshAccount();
        if (account?.quota) {
          this.applyQuota(account.quota);
        }
        if (account?.quota && account.pets.length && this.creationMode === "new" && !account.quota.canCreatePet) {
          await this.selectExistingPet(account.pets[0].petId);
        }
        return account;
      } catch (error) {
        if (isUnauthorizedApiError(error)) {
          auth.logout();
        }
        // 账号状态刷新失败不阻断已提交的生成任务，下一次操作还会以后端为准校验。
        return null;
      }
    },
    async restoreLatestJobForCurrentPet() {
      if (!this.currentPetId) {
        return;
      }

      try {
        const latestJob = await getLatestGenerationJobForPet(this.currentPetId);
        if (!latestJob) {
          return;
        }

        this.currentJobId = latestJob.jobId;
        this.currentPetId = latestJob.petId || this.currentPetId;
        this.selectedActionKeys = latestJob.actions.map((action) => action.key);
        for (const action of latestJob.actions) {
          if (!this.results[action.key]) {
            this.results[action.key] = {
              key: action.key,
              label: action.label,
              status: "idle",
              regenerateUsed: false
            };
          }
        }
        this.applyBackendJob(latestJob);
        this.syncStatus = latestJob.actions.length > 0 && latestJob.actions.every((action) => action.status === "complete") ? "ready" : "waiting";
        this.packageStatus = await getPetPackageStatus(this.currentPetId).catch(() => null);
        if (this.packageStatus?.status === "ready") {
          this.syncStatus = "synced";
        }
      } catch (error) {
        if (isUnauthorizedApiError(error)) {
          useAuthStore().logout();
          return;
        }
        // 历史动作恢复失败不阻断当前页面，用户仍可重新生成。
      }
    }
  }
});

function waitForBackendStep() {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, 1800);
  });
}

function waitForPaymentStep() {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, 2200);
  });
}

function redirectToPaymentPage(url: string) {
  globalThis.location.assign(url);
}

function messageFromRequestError(error: unknown, fallback: string) {
  if (isUnauthorizedApiError(error)) {
    useAuthStore().logout();
    return t("store.unauthorized");
  }

  return error instanceof Error ? error.message : fallback;
}

function paymentErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiRequestError && error.code === "payment_provider_not_configured") {
    return t("store.paymentProviderSetup");
  }

  const message = messageFromRequestError(error, fallback);
  return isPaymentGatewaySetupMessage(message) ? fallback : message;
}

function isPaymentGatewaySetupMessage(message: string) {
  return message.includes("支付网关") && message.includes("暂未配置");
}

function formatFileSize(bytes: number) {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  }

  return `${Math.max(1, Math.ceil(bytes / 1024))}KB`;
}

function warmJobMessage(petName: string, userAlias: string, message: string) {
  const name = petName || t("store.fallbackPetName");
  const alias = userAlias || t("store.fallbackAlias");
  if (!message) {
    return t("store.warmDefault", { petName: name, userAlias: alias });
  }

  if (message.includes("后端") || message.includes("接口") || message.includes("任务")) {
    if (message.includes("失败")) {
      return t("store.warmFailed", { petName: name, userAlias: alias });
    }
    if (message.includes("视频")) {
      return t("store.warmVideo", { petName: name, userAlias: alias });
    }
    return t("store.warmDefault", { petName: name, userAlias: alias });
  }

  return message;
}

function warmPackageMessage(petName: string, userAlias: string, message?: string) {
  const name = petName || t("store.fallbackPetName");
  const alias = userAlias || t("store.fallbackAlias");
  if (!message || message.includes("后端") || message.includes("皮肤包") || message.includes("building")) {
    return t("store.warmPackage", { petName: name, userAlias: alias });
  }

  return message;
}

function desktopPairingReadyMessage(petName: string, userAlias: string, copied = false) {
  const name = petName || t("store.fallbackPetName");
  const alias = userAlias || t("store.fallbackAlias");
  if (copied) {
    return t("store.pairingCopied", { petName: name, userAlias: alias });
  }

  return t("store.pairingReady", { petName: name, userAlias: alias });
}
